import {useEffect, useMemo, useRef, useState} from 'react'
import {FilePond, registerPlugin} from 'react-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
import './styles.css'
import * as Brand from '@brand';
import BrandLogo from "./Logo";
import {Helmet} from "react-helmet";
import RSCClipLoader from "react-spinners/ClipLoader"
import * as Sentry from "@sentry/react";
import {formatEta, formatSpeed, reportSentryError, withRetries} from "./util.jsx";
import {Estimation} from 'arrival-time';
import {debounce} from "lodash";
import {ProgressBar} from "./ProgressBar.jsx";

Sentry.init({
    dsn: "https://339edb9f7384988024c1b8c90b571d66@o4510038356000768.ingest.us.sentry.io/4510343200833536",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    enabled: process.env.NODE_ENV === 'production',
    integrations: [
        Sentry.replayIntegration()
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

registerPlugin(
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
    FilePondPluginImagePreview
)

function randomSessionId() {
    const uuid = crypto.randomUUID?.()
        ?? ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
            (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
        );
    return Date.now().toString(36) + '-' + uuid.slice(0, 8);
}

function getOrCreateDeviceId() {
    const key = 'air_device_id';
    let id = localStorage.getItem(key);
    if (!id) {
        id = randomSessionId();
        localStorage.setItem(key, id);
    }
    return id;
}

const useToken = () => {
    const [pin, setPin] = useState('');
    const [token, setToken] = useState(null);
    const [projectFolder, setProjectFolder] = useState(null);
    const [sessionFolder, setSessionFolder] = useState(null);

    return {token, setToken, pin, setPin, projectFolder, setProjectFolder, sessionFolder, setSessionFolder}
}


function useQuery() {
    return new URLSearchParams(location.search);
}

function PinBoxes({value, onChange, disabled}) {
    const refs = [useRef(), useRef(), useRef(), useRef()];
    const vals = (value || '').padEnd(4, ' ').slice(0, 4).split('');

    const setAt = (i, v) => {
        const s = (value || '');
        const next = (s.slice(0, i) + v.replace(/\D/g, '').slice(-1) + s.slice(i + 1)).slice(0, 4);
        onChange(next);
    };

    const handleKey = (i, e) => {
        if (e.key === 'Backspace' && !vals[i] && i > 0) refs[i - 1].current?.focus();
        if (/^\d$/.test(e.key)) {
            e.preventDefault();
            setAt(i, e.key);
            if (i < 3) refs[i + 1].current?.focus();
        }
    };

    const handleChange = (i, e) => {
        const v = e.target.value.replace(/\D/g, '');
        if (!v) {
            onChange((value || '').slice(0, i) + ' ' + (value || '').slice(i + 1));
            return;
        }
        setAt(i, v.slice(-1));
        if (i < 3) refs[i + 1].current?.focus();
    };

    const handlePaste = (e) => {
        const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 4);
        if (!text) return;
        e.preventDefault();
        onChange(text.padEnd(4, ' '));
        refs[Math.min(text.length, 3)].current?.focus();
    };

    return (
        <div onPaste={handlePaste} style={{display: 'flex', gap: 8}}>
            {[0, 1, 2, 3].map(i => (
                <input key={i}
                       ref={refs[i]}
                       inputMode="numeric" pattern="\d*"
                       maxLength={1}
                       value={vals[i] === ' ' ? '' : vals[i]}
                       onKeyDown={(e) => handleKey(i, e)}
                       onChange={(e) => handleChange(i, e)}
                       disabled={disabled}
                       aria-label={`PIN digit ${i + 1}`}
                       className="pin-box"
                />
            ))}
        </div>
    );
}

function pondErrorMessage(error) {
    if (!error) return 'Unknown error';
    const body = error.body;
    if (typeof body === 'string') return body;
    return body?.message ?? body?.message_short ?? error?.message ?? String(error);
}

function formatSessionDate(ts) {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})
        + ' at '
        + d.toLocaleTimeString(undefined, {hour: 'numeric', minute: '2-digit'});
}

function formatBytes(bytes) {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0, v = bytes;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function SessionsPicker({sessions, onResume, onStartFresh}) {
    return (
        <div className="login-wrap">
            <div className="login-hero">
                <BrandLogo size={64} style={{margin: '0 auto 16px'}}/>
                <h1>{Brand.brand.name}</h1>
                <p>We found previous upload sessions on this device.</p>
            </div>

            <div className="login-card">
                <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                    {sessions.map(s => (
                        <button
                            key={s.sessionId}
                            className="session-item"
                            onClick={() => onResume(s)}
                        >
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{fontWeight: 600, fontSize: 14}}>{formatSessionDate(s.startedAt)}</span>
                                <span style={{fontSize: 12, color: 'var(--muted)'}}>
                                    {s.files.length} file{s.files.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            {s.files.length > 0 && (
                                <div style={{fontSize: 12, color: 'var(--muted)', marginTop: 4, textAlign: 'left'}}>
                                    {s.files.slice(0, 3).map(f => f.filename).join(', ')}
                                    {s.files.length > 3 ? ` +${s.files.length - 3} more` : ''}
                                </div>
                            )}
                        </button>
                    ))}
                </div>

                <button className="btn-secondary" onClick={onStartFresh} style={{marginTop: 4}}>
                    Start a new session
                </button>
            </div>

            <p className="brand">{Brand.brand.name} © {new Date().getFullYear()}</p>
        </div>
    );
}

export default function App() {
    const deviceId = useMemo(() => getOrCreateDeviceId(), []);
    const sessionId = useMemo(() => randomSessionId(), []);

    const qp = new URLSearchParams(location.search)
    const [project, setProject] = useState(qp.get('project') || '')
    const [name, setName] = useState('')
    const pondRef = useRef(null)
    const [error, setError] = useState(null)

    const [pin, setPin] = useState('');
    const [loading, setLoading] = useState(false);

    // const isLocal = location.hostname === 'localhost' || location.hostname.startsWith('127.');
    const isLocal = false;

    const estimationRef = useRef(new Estimation());

    const [uploads, setUploads] = useState({});
// uploads[fileId] = { loaded, total }

    const [failedFiles, setFailedFiles] = useState([]); // { id, name } objects for files that failed after all retries

    // Sessions state
    const [pastSessions, setPastSessions] = useState(null); // null = not yet fetched
    const [showSessionsPicker, setShowSessionsPicker] = useState(false);
    const [activeSession, setActiveSession] = useState(null); // the resumed/current session object
    const [skipUploaded, setSkipUploaded] = useState(true);
    const [currentSessionFiles, setCurrentSessionFiles] = useState([]); // files completed in this session

    // Warn user before closing tab while uploads are in progress or there are failed files
    useEffect(() => {
        const handler = (e) => {
            const activeUploads = Object.values(uploads).some(u => u.progress > 0 && u.progress < 1);
            if (activeUploads || failedFiles.length > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [uploads, failedFiles]);

    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadSpeed, setUploadSpeed] = useState(null);
    const [eta, setUploadEta] = useState(null);

    const debouncedUpdate = useMemo(
        () =>
            debounce((loaded, total, setUploadProgress, setUploadSpeed, setUploadEta) => {

                const {estimate, speed} = estimationRef.current.update(loaded, total);

                const safeSpeed =
                    Number.isFinite(speed) && speed > 0 && speed < 1e9 ? speed : null;
                const safeEta =
                    Number.isFinite(estimate) && estimate >= 0 ? estimate / 1000 : null;


                setUploadSpeed(safeSpeed);
                setUploadEta(safeEta);
            }, 250), // tweak window as you like
        []
    );

    useEffect(() => {

        const entries = Object.values(uploads);
        if (!entries.length) {
            setUploadProgress(0);
            setUploadEta(null);
            setUploadSpeed(null);
            return;
        }

        let total = 0;
        let loaded = 0;
        for (const {size = 0, progress = 0} of entries) {
            total += size;
            loaded += (progress ?? 0) * size;
        }

        if (!total) {
            setUploadProgress(0);
            setUploadEta(null);
            setUploadSpeed(null);
            return;
        }

        setUploadProgress(loaded / total);
        debouncedUpdate(loaded, total, setUploadProgress, setUploadSpeed, setUploadEta);

    }, [uploads]);

    // Keep etaRef in sync so the interval can read the latest ETA without a stale closure.
    useEffect(() => { etaRef.current = eta; }, [eta]);

    const skipUploadedRef = useRef(skipUploaded);
    skipUploadedRef.current = skipUploaded;

    const {token, setToken, projectFolder, setProjectFolder, sessionFolder, setSessionFolder} = useToken()

    const authed = !!token;

    // Must be declared after token — token is needed in the fetch call below.
    const jobCompletedRef    = useRef(false);
    const uploadStartFiredRef = useRef(false);
    const batchInfoRef        = useRef({fileCount: 0, totalSize: 0, filenames: []});
    const etaRef             = useRef(null);

    // The session ID actually used for uploads — either restored from a resumed session or the freshly generated one
    const activeSessionIdRef = useRef(sessionId);
    const activeProjectFolderRef = useRef(null);
    const activeSessionFolderRef = useRef(null);

    // Fire /api/complete-job once all files in this session have finished uploading.
    // Uses a ref guard so it only fires once per session even if uploads state
    // re-renders multiple times at progress === 1.
    useEffect(() => {
        const entries = Object.entries(uploads);

        if (
            entries.length === 0 ||       // nothing uploaded yet
            jobCompletedRef.current ||    // already fired for this session
            failedFiles.length > 0        // outstanding failures — wait for retries
        ) return;

        const allDone = entries.every(([, u]) => u.progress >= 1);
        if (!allDone) return;

        jobCompletedRef.current = true;

        const {fileCount, totalSize, filenames} = batchInfoRef.current;

        fetch('/api/complete-job', {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                ...(token ? {'Authorization': `Bearer ${token}`} : {}),
            },
            body: JSON.stringify({sessionId: activeSessionIdRef.current, fileCount, totalSize, filenames}),
        }).catch(e => console.warn('complete-job notify failed', e));

    }, [uploads, failedFiles, token, sessionId]);

    // Periodically update the Slack "Uploading..." message with the current ETA.
    // Only fires once the start notification has been posted and the job isn't done yet.
    useEffect(() => {
        if (!token || !sessionId) return;

        const interval = setInterval(() => {
            if (!uploadStartFiredRef.current || jobCompletedRef.current) return;
            if (etaRef.current == null) return;
            fetch('/api/update-eta', {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({sessionId: activeSessionIdRef.current, eta: etaRef.current}),
            }).catch(e => console.warn('update-eta failed', e));
        }, 30_000);

        return () => clearInterval(interval);
    }, [token, sessionId]);

    async function login() {
        setError('');
        if (!project) return setError('Please enter a project ID.');
        if (!/^\d{4}$/.test(pin.replace(/\s/g, ''))) return setError('PIN must be 4 digits.');
        setLoading(true);
        try {
            const res = await fetch('/api/login', {
                method: 'POST',
                headers: {'content-type': 'application/json'},
                body: JSON.stringify({jobId: project.trim(), pin: pin.replace(/\s/g, ''), sessionId, username: name})
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.token) throw new Error(data?.error || `Login failed (${res.status})`);
            setToken(data.token);
            setProjectFolder(data.data.projectFolder);
            setSessionFolder(data.data.sessionFolder);
            activeProjectFolderRef.current = data.data.projectFolder;
            activeSessionFolderRef.current = data.data.sessionFolder;

            // Fetch past sessions for this job + device
            const sessRes = await fetch(`/api/sessions?jobId=${encodeURIComponent(project.trim())}&deviceId=${encodeURIComponent(deviceId)}`, {
                headers: {'Authorization': `Bearer ${data.token}`}
            });
            if (sessRes.ok) {
                const sessData = await sessRes.json();
                const sessions = sessData.sessions || [];
                if (sessions.length > 0) {
                    setPastSessions(sessions);
                    setShowSessionsPicker(true);
                } else {
                    setPastSessions([]);
                }
            }

            return data.token;
        } catch (e) {
            reportSentryError(e, {stage: 'login-catch', project: project.trim()});
            setError(e.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    }

    async function ensureLogin() {
        if (token) return token;
        if (!project || !/^\d{4}$/.test(pin)) throw new Error('Enter job & 4-digit pin');
        const res = await fetch('/api/login', {
            method: 'POST',
            headers: {'content-type': 'application/json'},
            body: JSON.stringify({jobId: project, pin, sessionId})
        });
        if (!res.ok) throw new Error('Login failed');
        const {token: t} = await res.json();
        setToken(t);
        return t;
    }

    function handleResume(session) {
        // Restore the session ID and folders from the chosen past session
        activeSessionIdRef.current = session.sessionId;
        activeProjectFolderRef.current = session.projectFolder;
        activeSessionFolderRef.current = session.sessionFolder;
        setActiveSession(session);
        setShowSessionsPicker(false);
    }

    function handleStartFresh() {
        // Keep the freshly generated sessionId and the folders from login
        activeSessionIdRef.current = sessionId;
        activeProjectFolderRef.current = projectFolder;
        activeSessionFolderRef.current = sessionFolder;
        setActiveSession(null);
        setShowSessionsPicker(false);
    }

    // Build a Set of "filename:size" for already-completed files — from resumed session + current session
    const completedFileKeys = useMemo(() => {
        const keys = new Set();
        for (const f of (activeSession?.files ?? [])) keys.add(`${f.filename}:${f.size}`);
        for (const f of currentSessionFiles) keys.add(`${f.filename}:${f.size}`);
        return keys;
    }, [activeSession, currentSessionFiles]);

    const completedFileKeysRef = useRef(completedFileKeys);
    completedFileKeysRef.current = completedFileKeys;

    const server = useMemo(() => ({
        process: async (fieldName, file, metadata, load, error, progress, abortFn) => {
            if (!project) {
                error('Please enter a project ID.');
                return abortFn && abortFn();
            }

            if (!name) {
                error('Please enter your name.');
                return abortFn && abortFn();
            }

            // Skip files already uploaded in the active session
            if (skipUploadedRef.current && completedFileKeysRef.current.has(`${file.name}:${file.size}`)) {
                setTimeout(() => {
                    try { pondRef.current?.removeFile(file.id); } catch (_) {}
                }, 50);
                return {abort: () => {}};
            }

            let authToken = await ensureLogin();

            // Tunables
            const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB — smaller chunks = shorter-lived connections, more resilient on flaky networks
            const CONCURRENCY = 4;
            const MAX_RETRIES = 5;
            const BASE_BACKOFF_MS = 1000;

            if (isLocal) {
                const qs = new URLSearchParams({
                    project,
                    name,
                    filename: file.name
                });

                // DEV: stream through the Function
                const xhr = new XMLHttpRequest();
                xhr.open('POST', `/api/upload?${qs}`);
                xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
                xhr.upload.onprogress = e => e.lengthComputable && progress(e.loaded / e.total);
                xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? load(file.name) : error(`upload ${xhr.status}`);
                xhr.onerror = () => error('network');
                xhr.send(file); // <- raw file body
                return {abort: () => xhr.abort()};
            }

            // State
            let aborted = false;
            const inflightXHRs = new Set();
            const partProgress = new Map(); // partNumber -> bytes uploaded
            let totalUploaded = 0;
            let objectKey, uploadId;

            const currentSessionId = activeSessionIdRef.current;
            const currentProjectFolder = activeProjectFolderRef.current;
            const currentSessionFolder = activeSessionFolderRef.current;

            const updateProgress = () => {
                let sum = 0;
                for (const v of partProgress.values()) sum += v || 0;
                // FilePond wants (computable, loaded, total)
                progress(true, sum, file.size);
            };

            const abortAll = () => {
                aborted = true;
                for (const xhr of inflightXHRs) {
                    try {
                        xhr.abort();
                    } catch (_) {
                    }
                }
                inflightXHRs.clear();
            };

            const uploadPartXHR = (url, blob, partNumber) =>
                new Promise((resolve, reject) => {

                    if (aborted) return reject(new Error('Aborted'));

                    const xhr = new XMLHttpRequest();
                    inflightXHRs.add(xhr);

                    const cleanup = () => inflightXHRs.delete(xhr);


                    xhr.upload.onprogress = (e) => {
                        if (!e.lengthComputable || aborted) return;
                        partProgress.set(partNumber, e.loaded);
                        updateProgress();
                    };

                    xhr.onload = () => {
                        cleanup();
                        if (aborted) return reject(new Error('aborted'));
                        if (xhr.status >= 200 && xhr.status < 300) {
                            try {
                                const {etag} = JSON.parse(xhr.responseText || '{}');
                                if (!etag) return reject(new Error(`No ETag for part ${partNumber}`));
                                // Lock part progress to full size on success
                                partProgress.set(partNumber, blob.size);
                                updateProgress();
                                resolve(etag);
                            } catch (e) {
                                reject(new Error(`Bad JSON for part ${partNumber}`));
                            }
                        } else {
                            reject(new Error(`Part ${partNumber} -> ${xhr.status}`));
                        }
                    };

                    xhr.onerror = () => {
                        cleanup();
                        if (aborted) return reject(new Error('aborted'));
                        reject(new Error(`Network error on part ${partNumber}`));
                    };

                    xhr.onabort = () => {
                        cleanup();
                        reject(new Error('aborted'));
                    };

                    xhr.open('PUT', url);
                    xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
                    xhr.send(blob);
                });

            const delay = (ms) => new Promise(r => setTimeout(r, ms));

            await (async () => {
                try {
                    // 1) INIT
                    {
                        const initURL = `/api/init-multipart?project=${encodeURIComponent(project)}`
                            + `&filename=${encodeURIComponent(file.name)}`
                            + `&type=${encodeURIComponent(file.type || '')}`
                            + `&session=${encodeURIComponent(currentSessionId)}`
                            + `&projectFolder=${encodeURIComponent(currentProjectFolder)}`
                            + `&sessionFolder=${encodeURIComponent(currentSessionFolder)}`
                        ;
                        const initData = await withRetries(async () => {
                            const res = await fetch(initURL, {
                                method: 'POST',
                                headers: {'Authorization': `Bearer ${authToken}`}
                            });
                            if (res.status === 401) {
                                try { authToken = await ensureLogin(); } catch (_) {}
                                throw new Error('init 401 - token refreshed, retrying');
                            }
                            if (!res.ok) {
                                throw new Error(`init ${res.status}`);
                            }
                            return res.json();
                        }, {
                            retries: 3,
                            baseDelay: 700,
                            shouldRetry: () => !aborted
                        });

                        ({objectKey, uploadId} = initData);
                    }

                    // 2) Build parts
                    const partsToDo = [];
                    let pn = 1;
                    for (let start = 0; start < file.size; start += CHUNK_SIZE) {
                        const end = Math.min(start + CHUNK_SIZE, file.size);
                        partsToDo.push({partNumber: pn, start, end, size: end - start});
                        partProgress.set(pn, 0);
                        pn++;
                    }

                    // 3) Worker pool
                    const results = []; // { partNumber, etag }
                    let nextIdx = 0;

                    const worker = async () => {
                        while (!aborted) {
                            const idx = nextIdx++;
                            if (idx >= partsToDo.length) return;

                            const {partNumber, start, end} = partsToDo[idx];
                            const chunk = file.slice(start, end);

                            let attempt = 0;
                            while (true) {
                                try {
                                    const url = `/api/upload-part?uploadId=${encodeURIComponent(uploadId)}&partNumber=${partNumber}&key=${encodeURIComponent(objectKey)}`;
                                    const etag = await uploadPartXHR(url, chunk, partNumber);
                                    results.push({partNumber, etag});
                                    break; // done with this part
                                } catch (e) {
                                    attempt++;
                                    if (aborted) throw e;
                                    if (attempt > MAX_RETRIES) throw e;
                                    // If 401, refresh auth token before retrying
                                    if (e.message?.includes('401')) {
                                        try { authToken = await ensureLogin(); } catch (_) {}
                                    }
                                    // backoff
                                    await delay(BASE_BACKOFF_MS * Math.pow(2, attempt - 1));
                                }
                            }
                        }
                    };

                    const workers = Array.from({length: Math.min(CONCURRENCY, partsToDo.length)}, () => worker());
                    await Promise.all(workers);

                    if (aborted) throw new Error('aborted');

                    // 4) COMPLETE (sort by partNumber as required)
                    results.sort((a, b) => a.partNumber - b.partNumber);
                    {
                        await withRetries(async () => {
                            const res = await fetch('/api/complete-multipart', {
                                method: 'POST',
                                headers: {'content-type': 'application/json', 'Authorization': `Bearer ${authToken}`},
                                body: JSON.stringify({uploadId, key: objectKey, parts: results})
                            });
                            if (res.status === 401) {
                                try { authToken = await ensureLogin(); } catch (_) {}
                                throw new Error('complete 401 - token refreshed, retrying');
                            }
                            if (!res.ok) {
                                throw new Error(`complete ${res.status}`);
                            }
                        }, {
                            retries: 3,
                            baseDelay: 700,
                            shouldRetry: () => !aborted
                        });

                        // Record completed file in D1
                        fetch('/api/record-file', {
                            method: 'POST',
                            headers: {'content-type': 'application/json', 'Authorization': `Bearer ${authToken}`},
                            body: JSON.stringify({
                                sessionId: currentSessionId,
                                deviceId,
                                projectFolder: currentProjectFolder,
                                sessionFolder: currentSessionFolder,
                                filename: file.name,
                                size: file.size,
                            }),
                        }).catch(e => console.warn('record-file failed', e));

                        progress(true, file.size, file.size);
                        load(objectKey);
                    }

                    // Snap progress to 1.0 and finish
                    progress(true, file.size, file.size);
                    load(objectKey);
                } catch (e) {
                    if (!aborted && uploadId && objectKey) {
                        // Best-effort server-side abort
                        try {
                            await fetch('/api/abort-multipart', {
                                method: 'POST',
                                headers: {'content-type': 'application/json', 'Authorization': `Bearer ${authToken}`},
                                body: JSON.stringify({uploadId, key: objectKey})
                            });
                        } catch (_) {
                        }
                    }
                    error(e?.message || 'Upload failed');

                    if (!aborted) {
                        reportSentryError(e, {
                            stage: 'multipart-upload', project,
                            fileName: file?.name,
                            fileSize: file?.size,
                            uploadId,
                            objectKey
                        });
                    }
                }
            })();

            // FilePond abort contract
            return {
                abort: () => {
                    abortAll();
                    abortFn && abortFn();
                }
            };
        },
        revert: null, fetch: null, load: null, restore: null
    }), [authed, token, project, deviceId]);


    // Tweak labels minimally
    const labelIdle = 'Drag & drop files or <span class="filepond--label-action">browse</span>'


    return (
        <div style={{height: '100dvh', overflowY: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', background: 'var(--bg)'}}>

            <Helmet><title>{`${Brand.brand.name} | ${new Date().getFullYear()}`}</title></Helmet>

            {!authed ? (
                <div className="login-wrap">
                    <div className="login-hero">
                        <BrandLogo size={64} style={{margin: '0 auto 16px'}}/>
                        <h1>{Brand.brand.name}</h1>
                        <p>Please enter your job & PIN to continue</p>
                    </div>

                    <div className="login-card">
                        <div>
                            <label className="field-label">Job code</label>
                            <input
                                type="text"
                                placeholder="e.g. CC-2200"
                                value={project}
                                onChange={e => setProject(e.target.value)}
                                disabled={!!qp.get('project') || loading}
                            />
                        </div>

                        <div>
                            <label className="field-label">PIN</label>
                            <PinBoxes value={pin} onChange={setPin} disabled={loading}/>
                        </div>

                        <div>
                            <label className="field-label">Your name</label>
                            <input
                                placeholder="e.g. Steve"
                                type="text"
                                onChange={e => setName(e.target.value)}
                            />
                        </div>

                        {error && <div className="error-banner">{error}</div>}

                        <button className="btn-primary" onClick={login} disabled={loading}>
                            {loading ? <Spinner size={18}/> : 'Continue'}
                        </button>
                    </div>

                    <p className="brand">{Brand.brand.name} © {new Date().getFullYear()}</p>
                </div>
            ) : showSessionsPicker ? (
                <SessionsPicker
                    sessions={pastSessions}
                    onResume={handleResume}
                    onStartFresh={handleStartFresh}
                />
            ) : (
                <div className="card">
                    <div className="header">
                        <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10}}>
                            <BrandLogo size={28} style={{borderRadius: 7, flexShrink: 0}}/>
                            <h1 style={{marginBottom: 0}}>Upload files</h1>
                        </div>
                        <p className="subtext">Please ensure all your files have completed uploading before leaving this page.</p>
                        <div style={{marginTop: 12}}>
                            <input
                                disabled={!!qp.get('project')}
                                type="text"
                                placeholder="Project code (e.g. CC-2200)"
                                value={project}
                                onChange={e => setProject(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="content">
                        {failedFiles.length > 0 && (
                            <div style={{
                                background: 'rgba(255,59,48,0.1)', borderRadius: 8,
                                padding: '10px 14px', marginBottom: 12
                            }}>
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6}}>
                                    <span style={{fontSize: 13, fontWeight: 500, color: 'var(--red)'}}>
                                        {failedFiles.length} file{failedFiles.length > 1 ? 's' : ''} failed after multiple retries
                                    </span>
                                    <button
                                        onClick={() => {
                                            failedFiles.forEach(({id}) => {
                                                try { pondRef.current?.processFile(id); } catch (_) {}
                                            });
                                        }}
                                        style={{
                                            fontSize: 12, fontWeight: 600, color: 'var(--red)',
                                            background: 'none', border: '1px solid var(--red)',
                                            borderRadius: 6, padding: '3px 10px', cursor: 'pointer'
                                        }}
                                    >
                                        Retry all
                                    </button>
                                </div>
                                <ul style={{fontSize: 12, color: 'var(--red)', paddingLeft: 16}}>
                                    {failedFiles.map(f => <li key={f.id}>{f.name}</li>)}
                                </ul>
                            </div>
                        )}

                        {error && <div className="error-banner" style={{marginBottom: 12}}>{error}</div>}

                        <FilePond
                            ref={pondRef}
                            allowMultiple
                            maxFileSize={200 * 1024 * 1024 * 1024}
                            credits={false}
                            acceptedFileTypes={[
                                "image/jpeg", "image/png", "image/heic", "image/heif",
                                "image/tiff", "image/webp",
                                "image/x-canon-cr2", "image/x-canon-cr3", "image/x-nikon-nef",
                                "image/x-sony-arw", "image/x-panasonic-raw", "image/x-olympus-orf",
                                "image/x-fuji-raf", "application/octet-stream",
                                "video/mp4", "video/quicktime", "video/x-matroska", "video/avi", "video/mpeg",
                                "audio/mpeg", "audio/wav", "audio/aac", "audio/flac",
                                "application/pdf", "image/*", "video/*", "audio/*", "application/octet-stream"
                            ]}
                            server={server}
                            labelIdle={labelIdle}
                            instantUpload
                            dropOnPage={true}
                            dropValidation={true}
                            allowReorder={false}
                            allowImagePreview={false}
                            labelFileProcessingError={error => {
                                const msg = pondErrorMessage(error);
                                reportSentryError(new Error(msg), {stage: 'filepond-processing-error', raw: error});
                                return msg;
                            }}
                            onerror={(error, file, status) => {
                                const msg = pondErrorMessage(error);
                                setError(msg);
                                const id = file?.id;
                                const name = file?.file?.name || file?.filename || 'Unknown file';
                                setFailedFiles(prev => prev.some(f => f.id === id) ? prev : [...prev, {id, name}]);
                                reportSentryError(new Error(msg), {stage: 'filepond-error', status, fileName: file?.filename, raw: error});
                            }}
                            onwarning={(error, file, status) => {
                                const msg = pondErrorMessage(error);
                                setError(msg);
                                reportSentryError(new Error(msg), {stage: 'filepond-warning', status, fileName: file?.filename, raw: error});
                            }}
                            oninitfile={file => {
                                setError(null);
                                const total = file.fileSize || file.file?.size || 0;
                                setUploads(prev => ({...prev, [file.id]: {progress: 0, size: total}}));
                            }}
                            oninit={() => {
                                setUploads({});
                                setUploadProgress(0);
                                setUploadEta(null);
                                setUploadSpeed(null);
                                setFailedFiles([]);
                                setCurrentSessionFiles([]);
                                estimationRef.current.update(0, 1);
                                jobCompletedRef.current = false;
                                uploadStartFiredRef.current = false;
                            }}
                            onprocessfilestart={(file) => {
                                const total = file.fileSize || file.file?.size || 0;
                                const fileId = file.id;

                                // If this file is going to be skipped, don't trigger notifications
                                if (skipUploadedRef.current && completedFileKeysRef.current.has(`${file.file?.name || file.filename}:${total}`)) return;

                                setFailedFiles(prev => prev.filter(f => f.id !== fileId));
                                setUploads(prev => ({...prev, [fileId]: {progress: 0, size: total}}));
                                if (jobCompletedRef.current) {
                                    jobCompletedRef.current = false;
                                    uploadStartFiredRef.current = false;
                                    batchInfoRef.current = {fileCount: 0, totalSize: 0, filenames: []};
                                }
                                if (!uploadStartFiredRef.current && token) {
                                    uploadStartFiredRef.current = true;
                                    // Reset ETA estimation from this moment so idle time on the
                                    // login/file-selection screen doesn't inflate the estimate.
                                    estimationRef.current.reset();
                                    const allFiles  = pondRef.current?.getFiles() ?? [];
                                    const filenames = allFiles.map(f => f.file?.name ?? f.filename).filter(Boolean);
                                    const totalSize = allFiles.reduce((s, f) => s + (f.fileSize || f.file?.size || 0), 0);
                                    const fileCount = allFiles.length;
                                    batchInfoRef.current = {fileCount, totalSize, filenames};
                                    fetch('/api/notify-upload-start', {
                                        method: 'POST',
                                        headers: {'content-type': 'application/json', 'Authorization': `Bearer ${token}`},
                                        body: JSON.stringify({sessionId: activeSessionIdRef.current, fileCount: allFiles.length, totalSize, filenames}),
                                    }).catch(e => console.warn('notify-upload-start failed', e));
                                }
                            }}
                            onprocessfileprogress={(file, progress) => {
                                const total = file.fileSize || file.file?.size || 0;
                                setUploads(prev => ({...prev, [file.id]: {progress: progress ?? 0, size: total}}));
                            }}
                            onprocessfile={(error, file) => {
                                if (error) return;
                                const size = file.fileSize || file.file?.size || 0;
                                const filename = file.file?.name || file.filename;
                                setUploads(prev => ({...prev, [file.id]: {size, progress: 1}}));
                                if (filename) {
                                    setCurrentSessionFiles(prev => prev.some(f => f.filename === filename && f.size === size) ? prev : [...prev, {filename, size}]);
                                }
                            }}
                            onremovefile={(_error, file) => {
                                if (!file) return;
                                setFailedFiles(prev => prev.filter(f => f.id !== file.id));
                                setUploads(prev => { const next = {...prev}; delete next[file.id]; return next; });
                            }}
                        />

                        {/* Already uploaded files from resumed session */}
                        {activeSession?.files?.length > 0 && (
                            <div style={{marginTop: 16}}>
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
                                    <span style={{fontSize: 13, fontWeight: 500, color: 'var(--muted)'}}>
                                        Already uploaded ({activeSession.files.length} file{activeSession.files.length !== 1 ? 's' : ''})
                                    </span>
                                    <label style={{display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--muted)', cursor: 'pointer', userSelect: 'none'}}>
                                        Skip re-uploading
                                        <input
                                            type="checkbox"
                                            checked={skipUploaded}
                                            onChange={e => setSkipUploaded(e.target.checked)}
                                            style={{display: 'none'}}
                                        />
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center',
                                            width: 32, height: 18, borderRadius: 999,
                                            background: skipUploaded ? '#3a9c52' : 'rgba(0,0,0,0.15)',
                                            transition: 'background 0.2s',
                                            padding: 2, flexShrink: 0,
                                        }}>
                                            <span style={{
                                                width: 14, height: 14, borderRadius: '50%',
                                                background: '#fff',
                                                transform: skipUploaded ? 'translateX(14px)' : 'translateX(0)',
                                                transition: 'transform 0.2s',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            }}/>
                                        </span>
                                    </label>
                                </div>
                                <ul style={{listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4}}>
                                    {activeSession.files.map(f => (
                                        <li key={f.filename} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            fontSize: 12, padding: '5px 8px', borderRadius: 6,
                                            background: 'rgba(52,199,89,0.08)', color: 'var(--subtext)'
                                        }}>
                                            <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8, color: '#3a9c52', fontWeight: 500}}>
                                                ✓ {f.filename}
                                            </span>
                                            <span style={{flexShrink: 0, color: 'var(--muted)'}}>{formatBytes(f.size)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="footer">
                        {uploadProgress != null && uploadProgress > 0 && (
                            <>
                                <div className="upload-status">
                                    <div className="upload-status-row">
                                        <span className="upload-status-label">{uploadProgress >= 1 ? 'Done' : 'Uploading files'}</span>
                                        <span className="upload-status-meta">
                                            {Math.round(uploadProgress * 100)}%
                                            {eta != null && eta > 0 && ` • ${formatEta(eta)}`}
                                            {uploadSpeed && ` • ${formatSpeed(uploadSpeed)}`}
                                        </span>
                                    </div>
                                </div>
                                <ProgressBar progress={uploadProgress * 100 + '%'}/>
                            </>
                        )}
                        <p className="brand">{Brand.brand.name} • {new Date().getFullYear()}</p>
                    </div>
                </div>
            )}
        </div>
    )
}

const Spinner = (props) => {
    return (<div className="mx-auto"><RSCClipLoader {...props}/></div>)
}
